<template>
  <v-container fluid fill-height pa-0>
    <v-stepper class="stepper" v-model="stepSelected" height="100%" width="100%">
      <v-stepper-header class="white sticky">
        <v-stepper-step :complete="stepSelected > 1" step="1">
          Select the region
          <small>{{ region }}</small>
        </v-stepper-step>
        <v-divider/>

        <v-stepper-step :complete="stepSelected > 2" step="2">
          Data Sources
        </v-stepper-step>
        <v-divider/>

        <v-stepper-step :complete="stepSelected > 3" step="3">Process the modules</v-stepper-step>
        <v-divider/>

        <v-stepper-step step="4">Results</v-stepper-step>
      </v-stepper-header>

      <v-stepper-items>
        <!-- Step 1: Select the region -->
        <v-stepper-content step="1" class="px-0 pb-0">
          <v-card flat>
            <v-card-text class="cardTextHeight">
              <v-row>
                <v-col sm="3">
                  <v-select v-model="regionSelected" :items="regions" item-text="REGION" return-object label="Region" prepend-icon="mdi-earth" outlined dense/>
                </v-col>
                <v-col sm="2">
                  <v-btn color="primary" outlined to="/regions">
                    <v-icon left>mdi-plus</v-icon>
                    New region
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>

            <v-card-actions class="px-12 primaryLight footer">
              <v-btn color="primary" dark @click="stepSelected = 2; createProcess()" :disabled="!regionSelected">Continue</v-btn> <!-- :disabled="!regionSelected" -->
              <!-- <v-btn color="red" dark @click="stepSelected = 3; tabSelected = 4">GO TO MOD 5 DEBUG</v-btn> -->
              <v-progress-circular color="primary" class="mx-4" v-if="isCreatingProcess" indeterminate :size="35"/>
            </v-card-actions>
          </v-card>
        </v-stepper-content>

        <!-- Step 2: Pre-module -->
        <v-stepper-content step="2" class="px-0 pb-0">
          <v-card flat>
            <vx-premodule ref="premodule" :region="regionSelected" :currentProcess="currentProcess" @onProcessed="processedPremodule"/>

            <v-card-actions class="px-12 primaryLight footer">
              <v-btn color="primary" dark @click="goToModules" :disabled="!isPremoduleProcessed">Continue</v-btn>
              <v-btn dark text @click="askRestartProcess">Restart</v-btn>
            </v-card-actions>
          </v-card>
        </v-stepper-content>

        <!-- Step 2: Modules -->
        <v-stepper-content step="3" class="pa-0">
          <v-card>
            <v-tabs class="tabs" v-model="tabSelected" ref="tabs" background-color="primaryLight" slider-color="red darken-1" slider-size="5" grow>
              <v-tab class="white--text" :disabled="(!plannedDeforestation && unplannedDeforestation)">Module 1</v-tab>
              <v-tab class="white--text" :disabled="!isModule1Processed || (plannedDeforestation && !unplannedDeforestation)">Module 2</v-tab>
              <v-tab class="white--text" :disabled="!isModule2Processed || (plannedDeforestation && !unplannedDeforestation)">Module 3</v-tab>
              <v-tab class="white--text" :disabled="!isModule3Processed">Module 4</v-tab>
              <v-tab class="white--text" :disabled="!isModule4Processed" v-show="isAvailableMod5">Module 5</v-tab>
            </v-tabs>

            <v-tabs-items v-model="tabSelected" class="moduleHeight">
              <!-- Module 1 -->
              <v-tab-item eager>
                <vx-module1 ref="module1" :region="regionSelected" :currentProcess="currentProcess" @onProcessed="processedModule1"/>
              </v-tab-item>

              <!-- Module 2 -->
              <v-tab-item eager>
                <vx-module2 ref="module2" :region="regionSelected" :currentProcess="currentProcess" @onProcessed="processedModule2"/>
              </v-tab-item>

              <!-- Module 3 -->
              <v-tab-item eager>
                <vx-module3 ref="module3" :region="regionSelected" :currentProcess="currentProcess" @onProcessed="processedModule3"/> <!-- :mod2Variables="mod2Variables" -->
              </v-tab-item>

              <!-- Module 4 -->
              <v-tab-item eager>
                <vx-module4 ref="module4" :region="regionSelected" :currentProcess="currentProcess" @onProcessed="processedModule4"/>
              </v-tab-item>

              <!-- Module 5 -->
              <v-tab-item eager>
                <vx-module5 ref="module5" :region="regionSelected" :currentProcess="currentProcess" @onProcessed="processedModule5"/>
              </v-tab-item>
            </v-tabs-items>

            <v-card-actions class="px-12 primaryLight footer">
              <v-btn color="primary" @click="goToResults" :disabled="!isModule4Processed">Continue</v-btn>
              <v-btn dark text @click="askRestartProcess">Restart</v-btn>
            </v-card-actions>
          </v-card>
        </v-stepper-content>

        <!-- Step 3: Results -->
        <v-stepper-content step="4" class="px-0 pb-0">
          <v-card flat>
            <vx-results class="cardTextHeight" :currentProcess="currentProcess"/>

            <v-card-actions class="px-12 primaryLight footer">
              <v-btn color="primary" @click="stepSelected = 3">Go back</v-btn>
              <v-btn color="green" @click="finish">Finish</v-btn>
            </v-card-actions>
          </v-card>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>

    <vx-upload-dialog v-model="showUploadDialog"/>

    <v-btn color="primary" fab x-large dark fixed bottom right @click="openUploadDialog" v-show="stepSelected !== 4">
      <v-icon>mdi-file-plus</v-icon>
    </v-btn>

    <v-overlay absolute :value="isProcessing">
      <v-progress-circular color="primary" indeterminate size="100" width="10"/>
    </v-overlay>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { v4 as uuidv4 } from 'uuid'

import api from '../api'

import Premodule from './modules/Premodule.vue'
import Module1 from './modules/Module1.vue'
import Module2 from './modules/Module2.vue'
import Module3 from './modules/Module3.vue'
import Module4 from './modules/Module4.vue'
import Module5 from './modules/Module5.vue'
import Results from './modules/Results.vue'

import UploadDialog from './files/UploadDialog.vue'

export default {
  name: 'Process',

  components: {
    'vx-premodule': Premodule,
    'vx-module1': Module1,
    'vx-module2': Module2,
    'vx-module3': Module3,
    'vx-module4': Module4,
    'vx-module5': Module5,
    'vx-results': Results,
    'vx-upload-dialog': UploadDialog
  },

  data: () => ({
    stepSelected: 1,
    regionSelected: null,
    tabSelected: 0, // Has to be 1 so that when user goes to step 2, we change the tab to 0 and it renders the red slider

    showUploadDialog: false,
    isCreatingProcess: false,
    currentProcess: '',

    // Premodule
    isPremoduleProcessed: false,
    plannedDeforestation: false,
    unplannedDeforestation: false,

    // Module 1
    isModule1Processed: false,

    // Module 2
    isModule2Processed: false,
    mod2Variables: [],

    // Module 3
    isModule3Processed: false,

    // Module 4
    isModule4Processed: false,

    // Module 5
    isModule5Processed: false,
    isAvailableMod5: true
  }),

  computed: {
    ...mapGetters('app', [
      'isProcessing'
    ]),
    ...mapGetters('auth', [
      'idUsuario'
    ]),
    ...mapGetters('region', [
      'regions'
    ]),
    region () {
      return (this.regionSelected && this.regionSelected.REGION) || ''
    }
  },

  methods: {
    ...mapActions('currentProcess', [
      'clearCurrentProcess'
    ]),

    ...mapActions('file', [
      'getFiles'
    ]),

    fixTabSlider () {
      this.$refs.tabs.callSlider()
    },

    openUploadDialog () {
      this.showUploadDialog = true
    },

    askRestartProcess () {
      var self = this
      this.$root.$confirmDialog.open('Restart', 'Are you sure you want to restart the process?').then(result => {
        if (result) {
          self.restartProcess()
        }
      })
    },

    async createProcess () {
      this.isCreatingProcess = true

      let processItem = {
        ID_PROCESO: uuidv4(),
        ID_REGION: this.regionSelected.ID_REGION,
        ID_USUARIO: this.idUsuario,
        FECHA: this.$date.getCurrentDateDB()
      }

      let response = await api.process.createProcess(processItem)
      if (response.data.success) {
        // console.log('New process created')
        this.currentProcess = response.data.data
        this.isCreatingProcess = false
      }
    },

    processedPremodule (data) {
      this.getFiles()

      this.isPremoduleProcessed = true

      this.plannedDeforestation = data.plannedDeforestation
      this.unplannedDeforestation = data.unplannedDeforestation
      this.isAvailableMod5 = data.isAvailableMod5
    },

    goToModules () {
      this.stepSelected = 3
      this.fixTabSlider()

      if (this.plannedDeforestation && !this.unplannedDeforestation) {
        this.tabSelected = 0
      } else if (!this.plannedDeforestation && this.unplannedDeforestation) {
        this.isModule1Processed = true
        this.tabSelected = 1
      } else if (this.plannedDeforestation && this.unplannedDeforestation) {
        this.tabSelected = 0
      }
    },

    processedModule1 () {
      this.getFiles()

      this.isModule1Processed = true
    },

    processedModule2 (data) {
      this.getFiles()

      this.isModule2Processed = true
      this.$refs.module3.initMod2Variables(data.mod2Variables)
    },

    processedModule3 () {
      this.getFiles()

      this.isModule3Processed = true
    },

    processedModule4 () {
      this.getFiles()

      this.isModule4Processed = true
    },

    processedModule5 () {
      // console.log('TODO processedModule5')
      this.getFiles()

      this.isModule5Processed = true
    },

    goToResults () {
      this.stepSelected = 4
    },

    async restartProcess (deleteProcess = true) {
      this.regionSelected = null
      this.stepSelected = 1
      this.tabSelected = null

      if (deleteProcess) {
        await api.process.deleteProcess({ ID_PROCESO: this.currentProcess })
        this.currentProcess = null
      }

      // Reset modules
      this.isPremoduleProcessed = false
      this.$refs.premodule.reset()
      this.isModule1Processed = false
      this.$refs.module1.reset()
      this.isModule2Processed = false
      this.$refs.module2.reset()
      this.isModule3Processed = false
      this.$refs.module3.reset()
      this.isModule4Processed = false
      this.$refs.module4.reset()
      this.isModule5Processed = false
      this.$refs.module5.reset()

      this.clearCurrentProcess()
    },

    finish () {
      var self = this
      this.$root.$confirmDialog.open('Finish', 'Are you sure you want to finish the process?').then(async result => {
        if (result) {
          self.restartProcess(false)
        }
      })
    }
  },

  mounted () {
  }
}
</script>

<style scoped>
  /* Sticky stepper */
  .stepper {
    overflow: visible;
  }
  .sticky {
    position: -webkit-sticky;
    position: sticky;
    top: 0;
    z-index: 2;
  }

  /* Sticky tabs (hay una regla a mayores en App.vue) */
  .tabs {
    position: -webkit-sticky;
    position: sticky;
    top: 72px;
    z-index: 1;
  }
  .v-stepper__items {
    overflow: visible !important;
  }

  /* Sticky footer */
  .footer {
    position: -webkit-sticky;
    position: sticky;
    bottom: 0;
    z-index: 1;
  }

  .cardTextHeight {
    min-height: calc(100vh - 212px);
  }

  .moduleHeight {
    min-height: calc(100vh - 236px);
  }
</style>
